/**
 * Converts an image URL to base64 encoding for use in PDFs
 * @param {string} url - The URL of the image to convert
 * @returns {Promise<string>} - Base64 encoded image
 */
import {getPhoto} from "../storageService";

export const fetchProviderImages = async (provider) => {
    try {
        const images = {
            profilePhoto: null,
            circularProfilePhoto: null,
            instagramIcon: null,
            phoneIcon: null,
        }

        // Fetch profile photo
        if (provider.photo) {
            try {
                const profilePhotoUrl = await getPhoto(provider.photo)
                images.profilePhoto = await convertImageToBase64(profilePhotoUrl)
                images.circularProfilePhoto = await createCircularImage(profilePhotoUrl)
            } catch (error) {
                console.error("Error fetching profile photo:", error)
            }
        }

        // Create social media icons
        images.instagramIcon = createSocialIcon("📷", "#000000")
        images.phoneIcon = createSocialIcon("📱", "#000000")

        return images
    } catch (error) {
        console.error("Error fetching provider images:", error)
        return {
            profilePhoto: null,
            circularProfilePhoto: null,
            instagramIcon: null,
            phoneIcon: null,
        }
    }
}

export const convertImageToBase64 = async (url) => {
    try {
        const response = await fetch(url)
        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`)
        }
        const blob = await response.blob()

        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => {
                resolve(reader.result)
            }
            reader.onerror = (e) => {
                console.error("Error in FileReader:", e)
                reject(new Error("FileReader error"))
            }
            reader.readAsDataURL(blob)
        })
    } catch (error) {
        console.error("Error converting image to base64:", error)
        throw new Error("Failed to convert image to base64: " + error.message)
    }
}

/**
 * Generates a QR code as a base64 data URL
 * @param {string} url - The URL to encode in the QR code
 * @returns {Promise<string>} - Base64 encoded QR code image
 */
export const generateQRCode = async (url) => {
    try {

        // Dynamically import QRCode to avoid issues
        const QRCodeModule = await import("qrcode")

        const qrCodeDataUrl = await QRCodeModule.toDataURL(url, {
            width: 200,
            margin: 1,
            color: {
                dark: "#000000",
                light: "#FFFFFF",
            },
        })

        return qrCodeDataUrl
    } catch (error) {
        console.error("Error generating QR code:", error)

        // Create a fallback QR code placeholder
        console.warn("Using fallback QR code placeholder")
        const canvas = document.createElement("canvas")
        canvas.width = 200
        canvas.height = 200
        const ctx = canvas.getContext("2d")

        // Fill background
        ctx.fillStyle = "#FFFFFF"
        ctx.fillRect(0, 0, 200, 200)

        // Draw border
        ctx.strokeStyle = "#000000"
        ctx.lineWidth = 5
        ctx.strokeRect(5, 5, 190, 190)

        // Add text
        ctx.fillStyle = "#000000"
        ctx.font = "16px Arial"
        ctx.textAlign = "center"
        ctx.fillText("QR Code", 100, 90)
        ctx.fillText("Error: " + error.message.substring(0, 20), 100, 110)

        return canvas.toDataURL("image/png")
    }
}

/**
 * Creates a circular image for the profile photo
 * @param {string} imageUrl - The URL of the image
 * @returns {Promise<string>} - Base64 encoded circular image
 */
export const createCircularImage = async (imageUrl) => {
    try {
        const img = new Image()
        img.crossOrigin = "anonymous"

        return new Promise((resolve, reject) => {
            img.onload = () => {
                const canvas = document.createElement("canvas")
                const ctx = canvas.getContext("2d")

                // Set canvas dimensions
                const size = Math.min(img.width, img.height)
                canvas.width = size
                canvas.height = size

                // Create circular clipping path
                ctx.beginPath()
                ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2, true)
                ctx.closePath()
                ctx.clip()

                // Draw image
                ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, size, size)

                // Add light gray border
                ctx.strokeStyle = "#CCCCCC"
                ctx.lineWidth = 5
                ctx.stroke()

                resolve(canvas.toDataURL("image/png"))
            }

            img.onerror = () => {
                reject(new Error("Failed to load image for circular cropping"))
            }

            img.src = imageUrl
        })
    } catch (error) {
        console.error("Error creating circular image:", error)
        throw new Error("Failed to create circular image")
    }
}

/**
 * Creates a social media icon with circle background
 * @param {string} emoji - The emoji to use as icon
 * @param {string} color - Background color
 * @returns {Promise<string>} - Base64 encoded icon
 */
export const createSocialIcon = (emoji, color = "#000000") => {
    try {
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")

        // Set canvas dimensions
        canvas.width = 40
        canvas.height = 40

        // Draw circle background
        ctx.beginPath()
        ctx.arc(20, 20, 18, 0, Math.PI * 2)
        ctx.fillStyle = color
        ctx.fill()

        // Draw emoji
        ctx.fillStyle = "#FFFFFF"
        ctx.font = "20px Arial"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(emoji, 20, 20)

        return canvas.toDataURL("image/png")
    } catch (error) {
        console.error("Error creating social icon:", error)
        return null
    }
}

/**
 * Loads an image and returns its dimensions
 * @param {string} url - The URL of the image
 * @returns {Promise<{width: number, height: number}>} - Image dimensions
 */
export const getImageDimensions = async (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = "anonymous"
        img.onload = () => {
            resolve({
                width: img.width,
                height: img.height,
            })
        }
        img.onerror = () => {
            reject(new Error("Failed to load image"))
        }
        img.src = url
    })
}
